import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import { EpisodeCard } from "../components/EpisodeCard";
import { usePlayer } from "../context/PlayerContext";
import { useSettings } from "../context/SettingsContext";
import { Play, Star, Headphones, Award, TrendingUp, CheckCircle2, Quote } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1540457036297-448b6b99e91c?w=1600&q=80";
const STAT_ICONS = [Award, Headphones, Star, TrendingUp];

export default function Home() {
  const [episodes, setEpisodes] = useState([]);
  const [latest, setLatest] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const { play } = usePlayer();
  const { settings } = useSettings();
  const home = settings?.home || {};

  useEffect(() => {
    api.get("/episodes").then((r) => setEpisodes(r.data.slice(0, 6)));
    api.get("/episodes/latest").then((r) => setLatest(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-petrol" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-[0.2em] font-semibold mb-6">
              <span className="w-8 h-px bg-gold" /> {home.hero_eyebrow || "Il Podcast di Consulenza del Lavoro"}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight text-balance">
              {home.hero_title || "Le novità del diritto del lavoro, spiegate da chi le vive ogni giorno"}
            </h1>
            <p className="text-lg text-slate-300 mt-6 leading-relaxed max-w-xl">
              {home.hero_subtitle || "Analisi normative, giurisprudenza e casi pratici per consulenti del lavoro, HR manager, avvocati giuslavoristi e imprenditori."}
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              {latest && (
                <button onClick={() => play(latest)} data-testid="hero-play-latest" className="flex items-center gap-2 bg-gold text-navy rounded-full px-8 py-3.5 font-semibold hover:bg-white transition-all shadow-lg">
                  <Play className="w-5 h-5" /> Ascolta l'ultimo episodio
                </button>
              )}
              <Link to="/abbonamenti" data-testid="hero-subscribe" className="flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white rounded-full px-8 py-3.5 font-medium hover:bg-white/20 transition-all">
                Abbonati
              </Link>
            </div>
          </div>

          {latest && (
            <div className="animate-fade-up bg-white/10 backdrop-blur-xl border border-white/15 rounded-2xl p-6" style={{ animationDelay: "0.15s" }}>
              <span className="text-xs uppercase tracking-widest text-gold font-semibold">Ultimo episodio</span>
              <div className="flex gap-4 mt-4">
                <img src={mediaUrl(latest.cover_url)} alt="" className="w-24 h-24 rounded-xl object-cover" />
                <div>
                  <h3 className="font-serif text-xl font-semibold text-white leading-snug line-clamp-2">{latest.title}</h3>
                  <p className="text-sm text-slate-300 mt-1">{latest.category} • {latest.duration}</p>
                </div>
              </div>
              <p className="text-sm text-slate-300 mt-4 line-clamp-3">{latest.description}</p>
              <button onClick={() => play(latest)} data-testid="hero-card-play" className="w-full mt-5 flex items-center justify-center gap-2 bg-white text-navy rounded-full py-3 font-semibold hover:bg-gold transition-colors">
                <Play className="w-4 h-4" /> Riproduci ora
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      {home.stats?.length > 0 && (
        <section className="border-b border-slate-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            {home.stats.map((s, i) => {
              const Icon = STAT_ICONS[i % STAT_ICONS.length];
              return (
                <div key={i} className="text-center" data-testid={`home-stat-${i}`}>
                  <Icon className="w-6 h-6 text-gold mx-auto mb-2" />
                  <div className="font-serif text-3xl font-bold text-navy">{s.value}</div>
                  <div className="text-sm text-slate-500 mt-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Latest episodes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-gold">Archivio</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mt-2">Ultimi episodi</h2>
          </div>
          <Link to="/episodi" data-testid="view-all-episodes" className="hidden sm:block text-navy font-medium hover:text-gold transition-colors">Vedi tutti →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {episodes.map((ep) => <EpisodeCard key={ep.id} ep={ep} />)}
        </div>
      </section>

      {/* Why follow */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={mediaUrl(settings?.host_photo_url)} alt={settings?.host_name || "Consulente del lavoro"} className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5]" />
            <div className="absolute -bottom-6 -right-6 bg-navy text-white rounded-2xl p-6 shadow-xl hidden sm:block">
              <div className="font-serif text-2xl font-bold text-gold">{settings?.host_name || "Enzo Lavolta"}</div>
              <div className="text-sm text-slate-300">{settings?.host_title || "Consulente del Lavoro"}</div>
            </div>
          </div>
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-gold">{home.why_title}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mt-2 mb-6">{home.why_heading}</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">{home.why_text}</p>
            <ul className="space-y-3">
              {(home.why_points || []).map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700"><CheckCircle2 className="w-5 h-5 text-emerald mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Link to="/chi-sono" data-testid="about-cta" className="inline-block mt-8 bg-navy text-white rounded-full px-8 py-3 font-medium hover:bg-petrol transition-all">Scopri di più</Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-14">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-gold">{home.reviews_title}</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mt-2">{home.reviews_heading}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <div key={t.id} className="bg-white border border-slate-100 rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <Quote className="w-8 h-8 text-gold/40 mb-4" />
              <p className="text-slate-600 leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}</div>
              <div className="font-semibold text-navy">{t.name}</div>
              <div className="text-sm text-slate-400">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4">Non perdere nessun aggiornamento</h2>
          <p className="text-lg text-slate-300 mb-8">Abbonati per accedere agli episodi premium, ai contenuti esclusivi e all'accesso anticipato.</p>
          <Link to="/abbonamenti" data-testid="cta-subscribe-bottom" className="inline-block bg-gold text-navy rounded-full px-10 py-4 font-semibold hover:bg-white transition-all shadow-lg">Scopri gli abbonamenti</Link>
        </div>
      </section>
    </div>
  );
}
